import { useCallback } from 'react';

import { CheckIcon, InfoIcon, XIcon, type LucideIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useBilling } from '@/modules/billing/hooks/useBilling';
import { isRequired } from '@/util/validation/constants';

const taxRegions: { value: string; label: string }[] = [
  { value: 'au_abn|Australia|AU', label: 'Australia' },
  { value: 'eu_vat|Austria|AT', label: 'Austria' },
  { value: 'eu_vat|Belgium|BE', label: 'Belgium' },
  { value: 'eu_vat|Bulgaria|BG', label: 'Bulgaria' },
  { value: 'eu_vat|Croatia|HR', label: 'Croatia' },
  { value: 'eu_vat|Cyprus|CY', label: 'Cyprus' },
  { value: 'eu_vat|Czech|CZ', label: 'Czech' },
  { value: 'eu_vat|Denmark|DK', label: 'Denmark' },
  { value: 'eu_vat|Estonia|EE', label: 'Estonia' },
  { value: 'eu_vat|Finland|FI', label: 'Finland' },
  { value: 'eu_vat|France|FR', label: 'France' },
  { value: 'eu_vat|Germany|DE', label: 'Germany' },
  { value: 'eu_vat|Greece|GR', label: 'Greece' },
  { value: 'eu_vat|Hungary|HU', label: 'Hungary' },
  { value: 'in_gst|India|IN', label: 'India' },
  { value: 'eu_vat|Ireland|IE', label: 'Ireland' },
  { value: 'eu_vat|Italy|IT', label: 'Italy' },
  { value: 'eu_vat|Latvia|LV', label: 'Latvia' },
  { value: 'eu_vat|Lithuania|LT', label: 'Lithuania' },
  { value: 'eu_vat|Luxembourg|LU', label: 'Luxembourg' },
  { value: 'eu_vat|Malta|MT', label: 'Malta' },
  { value: 'eu_vat|Netherlands|NL', label: 'Netherlands' },
  { value: 'nz_gst|New Zealand|NZ', label: 'New Zealand' },
  { value: 'no_vat|Norway|NO', label: 'Norway' },
  { value: 'eu_vat|Poland|PL', label: 'Poland' },
  { value: 'eu_vat|Portugal|PT', label: 'Portugal' },
  { value: 'eu_vat|Romania|RO', label: 'Romania' },
  { value: 'eu_vat|Slovakia|SK', label: 'Slovakia' },
  { value: 'eu_vat|Slovenia|SI', label: 'Slovenia' },
  { value: 'eu_vat|Spain|ES', label: 'Spain' },
  { value: 'eu_vat|Sweden|SE', label: 'Sweden' },
  { value: 'eu_vat|United Kingdom|GB', label: 'United Kingdom' },
  { value: 'other', label: 'Other' },
];

type VerificationStatus = 'verified' | 'pending' | 'unverified' | 'unavailable';
const verificationConfig: Record<
  VerificationStatus,
  { color: string; icon: LucideIcon; tooltip: string }
> = {
  verified: { color: 'text-success', icon: CheckIcon, tooltip: 'common.verified' },
  pending: { color: 'text-warning', icon: InfoIcon, tooltip: 'billing.tax.pending' },
  unverified: { color: 'text-destructive', icon: XIcon, tooltip: 'billing.tax.invalid_tax_id' },
  unavailable: { color: 'text-destructive', icon: XIcon, tooltip: 'billing.tax.invalid_tax_id' },
};

type FormValues = { region: string; value: string };
const FORM_ID = 'tax-id-form';

export const TaxRegionFieldset = () => {
  const { t } = useTranslation();
  const { canWriteBilling, updateTaxId, taxId, updateTaxIdStatus } = useBilling();

  const form = useForm<FormValues>({
    mode: 'onChange',
    values: { region: taxId?.region ?? 'other', value: taxId?.value ?? '' },
  });
  const {
    handleSubmit,
    control,
    formState: { isDirty },
  } = form;

  const onSubmit = useCallback(
    ({ region, value }: FormValues) => updateTaxId({ value, region }),
    [updateTaxId]
  );

  const verification = taxId?.verification?.status
    ? verificationConfig[taxId.verification.status]
    : null;

  return (
    <div className="flex flex-col gap-2">
      <h2 className="px-1.5 text-sm font-medium text-muted-foreground">
        {t('billing.tax.region')}
      </h2>
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID}>
          <FieldsetCard
            isDirty={isDirty}
            isLoading={updateTaxIdStatus === 'pending'}
            disabled={!isDirty}
            saveButtonLabel={t('common.save')}
            formId={FORM_ID}
          >
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={control}
                name="region"
                rules={isRequired}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('billing.tax.region')}</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={!canWriteBilling}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {taxRegions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="value"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('billing.tax.tax_id')}</FormLabel>
                    <div className="relative">
                      <FormControl>
                        <Input placeholder="GR12345678" {...field} />
                      </FormControl>
                      {verification && (
                        <div
                          className={cn(
                            'absolute top-1/2 right-3 -translate-y-1/2',
                            verification.color
                          )}
                        >
                          <InfoTooltip content={t(verification.tooltip)} side="top" showArrow>
                            <verification.icon size={18} />
                          </InfoTooltip>
                        </div>
                      )}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </FieldsetCard>
        </form>
      </Form>
    </div>
  );
};
